// Portfolio live, in place (wiki/pr/JOURNAL-REALTIME-PUSH-20261102.md):
//
//   - a re-read MERGES into the portfolio on screen: a holding that did not
//     change keeps its object, and when nothing changed at all the very same
//     portfolio comes back (no re-render, no flicker); a holding the screen
//     holds at a newer journal version keeps its pushed balance;
//   - a `jettons.balance` frame on jettons:<owner> moves one holding's
//     balance the instant it lands, by its version only: 0 closes the
//     position ("0"), an unknown balance (net < 0) is "—". Its value moves
//     with it at the holding's own price. A token the wallet had no row for
//     cannot be drawn from a balance alone: the caller re-reads.

import type { WalletPortfolio, WireHolding } from '../api/portfolio'
import type { WsMessage } from '../ws/WsClient'
import { JETTONS_BALANCE_EVENT, parseJournalBalance, rawToDecimal } from './journalFrames'
import { newerVersion } from './rowStore'
import { addressToRawForm } from './tonRawAddress'

const keyOf = (h: WireHolding) => addressToRawForm(h.token_master) ?? h.token_master

const same = (a: unknown, b: unknown) => a === b || JSON.stringify(a) === JSON.stringify(b)

/** The re-read holding with the screen's newer pushed balance kept. */
function keepPushed(server: WireHolding, shown: WireHolding): WireHolding {
  return {
    ...server,
    balance: shown.balance,
    balance_raw: shown.balance_raw,
    current_value_usd: shown.current_value_usd,
    current_value_ton: shown.current_value_ton,
    quantity_version: shown.quantity_version,
  }
}

const numOf = (v: string | undefined) => {
  const n = v == null || v.trim() === '' ? Number.NaN : Number(v)
  return Number.isFinite(n) ? n : null
}

/**
 * A re-read that did not price a row the screen shows priced keeps the shown
 * price (operator rule 2026-11-03: a known value leaves only on a proven
 * change, never because a read did not finish — the server once dropped 270
 * of 278 marks on every other read). Its value and PnL are restated at that
 * price on the re-read's own balance and basis, by the server's formula
 * (positions_read.go): value = balance × price; unrealized = known units ×
 * price − known basis; total = realized + unrealized. null: nothing to keep —
 * the re-read priced the row, the screen had no price, or the re-read states
 * no balance (unknown balance × price is not a value).
 */
function keepPrice(server: WireHolding, shown: WireHolding): WireHolding | null {
  if (server.price_available || !shown.price_available || server.position_unavailable) return null
  const price = numOf(shown.current_price_usd)
  const balance = numOf(server.balance)
  if (price === null || balance === null) return null
  const out: WireHolding = {
    ...server,
    price_available: true,
    current_price_usd: shown.current_price_usd,
    current_mcap_usd: shown.current_mcap_usd,
    current_value_usd: plain(balance * price),
  }
  const priceTon = numOf(shown.current_price_ton)
  if (priceTon !== null) {
    out.current_price_ton = shown.current_price_ton
    out.current_value_ton = plain(balance * priceTon)
  }
  const raw = server.valued_known_raw
  const dec = server.token_decimals
  const basis = numOf(server.remaining_cost_basis_usd)
  if (
    raw != null &&
    /^\d+$/.test(raw) &&
    typeof dec === 'number' &&
    Number.isInteger(dec) &&
    dec >= 0 &&
    basis !== null
  ) {
    const known = Number(rawToDecimal(BigInt(raw), dec))
    const unrealized = known * price - basis
    out.unrealized_pnl_usd = plain(unrealized)
    const realized = numOf(server.realized_pnl_usd)
    if (realized !== null) {
      out.total_pnl_usd = plain(realized + unrealized)
      const acquired = numOf(server.acquisition_basis_usd)
      if (acquired !== null && acquired > 0)
        out.total_pnl_pct = plain(((realized + unrealized) / acquired) * 100)
    }
  }
  return out
}

/** A row that holds something, or may (balance unknown). */
function held(h: WireHolding): boolean {
  if (h.position_unavailable) return true
  const b = numOf(h.balance)
  return b === null || b > 0
}

/**
 * The summary of a re-read whose rows were completed by keepPrice, restated by
 * the server's own rule (portfolioFromHoldings): the value is known when every
 * held row is priced and the list is whole; unrealized and total also need
 * every row's PnL complete. Anything the re-read itself left unknown for
 * another reason stays as sent.
 */
function restateTotals(next: WalletPortfolio, holdings: WireHolding[]): WalletPortfolio {
  if (next.price_coverage_complete || next.holdings_complete === false) return next
  if (!holdings.filter(held).every((h) => h.price_available)) return next
  let value = 0
  let unrealized = 0
  for (const h of holdings) {
    value += numOf(h.current_value_usd) ?? 0
    unrealized += numOf(h.unrealized_pnl_usd) ?? 0
  }
  const out: WalletPortfolio = {
    ...next,
    price_coverage_complete: true,
    total_current_value_usd: plain(value),
  }
  const realized = numOf(next.total_realized_pnl_usd)
  if (next.pnl_complete !== false && realized !== null) {
    out.total_unrealized_pnl_usd = plain(unrealized)
    out.total_pnl_usd = plain(realized + unrealized)
  }
  return out
}

/** The portfolio reads its balances from our journal (positions model,
    journal mode): its holdings carry quantity_version. */
export function isJournalPortfolio(p: WalletPortfolio | null): boolean {
  return p?.holdings.some((h) => h.quantity_version != null) === true
}

/** `next` merged onto `prev` in place (see the header). */
export function mergePortfolio(
  prev: WalletPortfolio | null,
  next: WalletPortfolio
): WalletPortfolio {
  if (prev == null) return next
  // another wallet's portfolio is never merged into: its versions are its own
  const wallet = (p: WalletPortfolio) => addressToRawForm(p.wallet_address) ?? p.wallet_address
  if (wallet(prev) !== wallet(next)) return next
  const shown = new Map<string, WireHolding>()
  for (const h of prev.holdings) shown.set(keyOf(h), h)
  let changed = prev.holdings.length !== next.holdings.length
  let kept = false
  const holdings = next.holdings.map((h, i) => {
    const s = shown.get(keyOf(h))
    let out = h
    if (s !== undefined) {
      if (newerVersion(s.quantity_version, h.quantity_version)) out = keepPushed(h, s)
      const priced = keepPrice(out, s)
      if (priced !== null) {
        out = priced
        kept = true
      }
      if (same(s, out)) out = s
    }
    if (out !== prev.holdings[i]) changed = true
    return out
  })
  const summary = kept ? restateTotals(next, holdings) : next
  const { holdings: _p, ...prevRest } = prev
  const { holdings: _n, ...nextRest } = summary
  if (!changed && same(prevRest, nextRest)) return prev
  return { ...summary, holdings: changed ? holdings : prev.holdings }
}

const num = (v: string | undefined) => {
  const n = v == null || v === '' ? Number.NaN : Number(v)
  return Number.isFinite(n) ? n : null
}

/** A plain decimal of a double, no exponent (the wire's own form). */
function plain(n: number): string {
  return Number.isInteger(n) ? n.toString() : n.toFixed(12).replace(/0+$/, '').replace(/\.$/, '')
}

/** One holding at its pushed journal balance. */
function holdingAt(h: WireHolding, amount: bigint | null, version: bigint): WireHolding {
  const out: WireHolding = { ...h, quantity_version: Number(version) }
  const dec = h.token_decimals
  if (amount == null || typeof dec !== 'number' || !Number.isInteger(dec) || dec < 0) {
    // unknown balance (or unknown decimals): "—", never a confident figure
    out.balance = ''
    out.balance_raw = amount == null ? undefined : amount.toString()
    out.current_value_usd = undefined
    out.current_value_ton = undefined
    return out
  }
  const balance = rawToDecimal(amount, dec)
  out.balance = balance
  out.balance_raw = amount.toString()
  const whole = Number(balance)
  const pUsd = num(h.current_price_usd)
  const pTon = num(h.current_price_ton)
  out.current_value_usd =
    pUsd != null ? plain(whole * pUsd) : amount === 0n ? '0' : h.current_value_usd
  out.current_value_ton =
    pTon != null ? plain(whole * pTon) : amount === 0n ? '0' : h.current_value_ton
  return out
}

/**
 * A `jettons.balance` frame on the portfolio on screen: the moved portfolio;
 * the same object when it is not this wallet's, not newer, or moves nothing;
 * 'reread' when it names a token the portfolio has no row for.
 */
export function applyJettonFrame(
  portfolio: WalletPortfolio,
  owner: string,
  msg: WsMessage
): WalletPortfolio | 'reread' {
  if (msg.event !== JETTONS_BALANCE_EVENT) return portfolio
  const f = parseJournalBalance(msg.data)
  if (f == null || f.owner !== (addressToRawForm(owner) ?? owner)) return portfolio
  const i = portfolio.holdings.findIndex((h) => keyOf(h) === f.master)
  if (i < 0) return f.amount == null || f.amount === 0n ? portfolio : 'reread'
  const h = portfolio.holdings[i] as WireHolding
  // a holding not read from the journal (chain / trades) is not moved by it
  if (h.quantity_version == null) return portfolio
  if (!newerVersion(f.version, h.quantity_version)) return portfolio
  const holdings = portfolio.holdings.slice()
  holdings[i] = holdingAt(h, f.amount, f.version)
  return { ...portfolio, holdings }
}
